"use client";

// #468 fill-communication PART A: per-box segmented size selector
// (Eighth/Quarter/Half). Only the sizes this box's denom OFFERS appear; QB is
// highlighted "best value"; a size the box already EXACTLY fills reads "ready";
// sizes smaller than the current bunch count are DISABLED (can't down-select
// below what's in the box). Tapping re-targets the box - the fill bar, "add M
// more", and the checkout gate all key off the SELECTED size via box-rules.
// Never renders a %. Reads/writes selected size through the cart store so it
// persists across re-materialize.
//
// #468 catalog bar (mockup): rendered as small PILL chips (not big squares),
// shared by the cart box header AND the catalog builder bar so both surfaces
// read identically. One line: "Eighth 8" (label + bold count) + a small suffix
// ("best value" / "ready") that hides on phones to stay one compact line.
// Colors are the portal tokens (brand-teal / brand-border / brand-text*) - the
// mockup is the SHAPE source, the portal tokens are the COLOR source.
import type { CartBox } from "@/lib/cart/types";
import type { BoxFill } from "@/lib/cart/box-rules";
import { sizeWord, spelledBoxSize } from "@/lib/cart/box-size-label";
import { useCartStore } from "@/stores/cart";

export function CartBoxSizeSelector({
  box,
  fill,
  // "stack" (default) keeps the cart-header top margin; "inline" drops it so the
  // catalog builder bar can sit the chips on the same baseline as the row.
  variant = "stack",
}: {
  box: CartBox;
  fill: BoxFill;
  variant?: "stack" | "inline";
}) {
  const setGroupSize = useCartStore((s) => s.setGroupSize);
  // #63 box_stem: a stem-fill box has mode "stems" but a POPULATED EB/QB ladder
  // (denoms in stems). Gate on the ladder, not the mode, so the SAME size chips
  // render for bunch boxes and stem boxes. Whole-box hydrangea + box-mode boxes
  // carry an empty ladder -> still null.
  if (fill.ladder.length < 2) return null;

  return (
    <div
      data-testid="cart-box-size-selector"
      className={
        (variant === "inline" ? "" : "mt-2 ") +
        "flex flex-wrap items-center gap-1.5"
      }
      role="group"
      aria-label="Choose box size"
    >
      {fill.ladder.map((rung) => {
        const label = spelledBoxSize(rung.size);
        const word = sizeWord(rung.size);
        const suffix = rung.readyNow
          ? "ready"
          : rung.best
            ? "best value"
            : "";
        return (
          <button
            key={rung.size}
            type="button"
            data-testid={`box-size-${rung.size}`}
            data-selected={rung.selected || undefined}
            data-ready={rung.readyNow || undefined}
            aria-pressed={rung.selected}
            title={
              rung.selected
                ? `This group is a ${label}.`
                : `Re-pack this group as ${label} boxes.`
            }
            // #468 SIZE DRIVES PACKING: tapping re-packs the whole GROUP to this
            // size (fewer/larger or more/smaller boxes). Every size is clickable.
            onClick={() => setGroupSize(box, rung.size)}
            className={[
              "inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1 text-[11px] font-medium leading-none transition-colors",
              rung.selected
                ? "border-brand-teal bg-brand-teal text-white"
                : rung.readyNow
                  ? "border-brand-teal bg-white text-brand-teal-dark hover:bg-brand-teal-soft"
                  : "border-brand-border bg-white text-brand-text-muted hover:border-brand-teal",
            ].join(" ")}
          >
            <span>
              {word}{" "}
              <span className="font-semibold">
                {rung.size} &middot; {rung.denom}
              </span>
            </span>
            {suffix && (
              <span className="hidden font-normal opacity-80 md:inline">
                &middot; {suffix}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
